import { useEffect, useState } from 'react';
import { X, GraduationCap } from 'lucide-react';

const STORAGE_KEY = 'maestro:pitch-banner-dismissed:v1';
const REOPEN_EVENT = 'maestro:show-pitch-banner';

/** Programmatically re-open the banner from anywhere in the app. */
export function reopenMaestroPitchBanner() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent(REOPEN_EVENT));
}

/**
 * One-time educational banner that explains Maestro's value proposition and
 * sets expectations about YouTube-hosted media (licensing / embeddability).
 * Dismissed permanently in localStorage. Can be re-opened via `reopenMaestroPitchBanner()`.
 */
export default function MaestroPitchBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch { /* SSR / privacy mode → keep hidden */ }
    const handler = () => setVisible(true);
    window.addEventListener(REOPEN_EVENT, handler);
    return () => window.removeEventListener(REOPEN_EVENT, handler);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="maestro-pitch-banner"
      className="mb-6 rounded-2xl border border-orange-800/60 bg-gradient-to-br from-orange-950/50 via-stone-900 to-stone-900 p-5 relative overflow-hidden"
    >
      <button
        data-testid="maestro-pitch-banner-close"
        onClick={dismiss}
        aria-label="Fermer"
        className="absolute top-3 right-3 p-1 rounded-full text-stone-400 hover:text-stone-100 hover:bg-stone-800/60 transition-colors"
      >
        <X size={16} />
      </button>
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-11 h-11 rounded-xl bg-orange-900/50 border border-orange-700/60 flex items-center justify-center text-orange-300">
          <GraduationCap size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline text-orange-400 mb-1">Bienvenue sur Maestro</div>
          <div className="font-display text-xl md:text-2xl text-stone-100 leading-tight mb-2">
            Un outil d'apprentissage, pas un service de streaming
          </div>
          <p className="text-sm text-stone-300 leading-relaxed">
            Les vidéos et audios sont <span className="text-stone-100 font-medium">streamés depuis YouTube</span> pour respecter les droits d'auteur.
            Certaines chansons peuvent être <span className="text-stone-100 font-medium">coupées</span>, non-embarquables, ou remplacées par des versions live/karaoké selon les ayants droit.
          </p>
          <p className="text-sm text-stone-400 mt-3 leading-relaxed">
            La vraie valeur de Maestro est <span className="text-orange-300 font-medium">à toi</span> : accords synchronisés,
            tablatures, cours structurés, IA tuteur, communauté, métronome intelligent, boucle de section, contrôle de vitesse…
            Tout ce qu'il faut pour <span className="text-orange-300 font-medium">apprendre vraiment</span>, à ton rythme.
          </p>
          <button
            onClick={dismiss}
            data-testid="maestro-pitch-banner-accept"
            className="mt-4 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-orange-700 hover:bg-orange-600 text-white text-sm font-medium transition-colors"
          >
            J'ai compris, on joue
          </button>
        </div>
      </div>
    </div>
  );
}
